/**
 * Discount Approval Service
 *
 * Handles barber strike tracking, discount approval workflows, and manager oversight.
 * Implements the AI-powered fraud prevention system with configurable strike limits.
 *
 * Key Features:
 * - Rolling period strike tracking (weekly, monthly, quarterly, yearly)
 * - Automatic approval/denial based on risk score and strike count
 * - Manager approval workflow with remote iPad notifications
 * - Strike increment/reset logic
 * - Owner escalation for critical violations
 *
 * Related: fraudDetectionService.ts, aiWorkflowEngine.ts
 */

import { db } from '../config/firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  Timestamp,
  orderBy,
  limit,
} from 'firebase/firestore';
import {
  DiscountRequest,
  FraudAnalysisResult,
  FraudSeverity,
} from './fraudDetectionService';

// ============================================================================
// INTERFACES & TYPES
// ============================================================================

/**
 * Strike tracking period - determines how far back to count strikes
 */
export type StrikePeriod = 'weekly' | 'monthly' | 'quarterly' | 'yearly';

/**
 * Approval decision status
 */
export type ApprovalStatus =
  | 'pending' // Waiting for manager review
  | 'auto_approved' // System approved automatically
  | 'manager_approved' // Manager manually approved
  | 'denied' // Denied by manager or system
  | 'expired'; // Request timed out

/**
 * Notification channel for manager alerts
 */
export type NotificationChannel = 'push' | 'sms' | 'email' | 'in_app';

/**
 * Barber strike record stored in Firebase
 */
export interface BarberStrike {
  id: string;
  barberId: string;
  barberName: string;
  discountRequestId: string;
  appointmentId: string;
  clientId: string;
  riskScore: number;
  severity: FraudSeverity;
  fraudFlags: string[];
  strikeReason: string;
  originalPrice: number;
  discountedPrice: number;
  discountAmount: number;
  discountPercent: number;
  paymentMethod: string;
  issuedAt: Date | Timestamp;
  issuedBy: 'system' | 'manager' | 'owner';
  notes?: string;
  appealedAt?: Date | Timestamp;
  appealStatus?: 'pending' | 'approved' | 'denied';
}

/**
 * Strike count summary for a barber
 */
export interface StrikeCount {
  barberId: string;
  barberName: string;
  weekly: number;
  monthly: number;
  quarterly: number;
  yearly: number;
  total: number;
  lastStrikeDate: Date | null;
  nextResetDate: Date | null;
}

/**
 * Discount approval request stored in Firebase
 */
export interface DiscountApprovalRequest {
  id: string;
  barberId: string;
  barberName: string;
  discountRequest: DiscountRequest;
  fraudAnalysis: FraudAnalysisResult;
  status: ApprovalStatus;
  strikeCountAtRequest: number;
  requiredApprovalLevel: 'none' | 'manager' | 'owner';
  requestedAt: Date | Timestamp;
  assignedManagerId?: string;
  assignedManagerName?: string;
  reviewedAt?: Date | Timestamp;
  reviewedBy?: string;
  reviewerRole?: 'manager' | 'owner';
  approvalNotes?: string;
  denialReason?: string;
  notificationsSent: NotificationChannel[];
  expiresAt?: Date | Timestamp;
  isExpired: boolean;
}

/**
 * Approval decision made by manager or system
 */
export interface ApprovalDecision {
  requestId: string;
  approved: boolean;
  reviewedBy: string;
  reviewerRole: 'manager' | 'owner';
  notes?: string;
  strikeIssued: boolean;
  strikeReason?: string;
}

/**
 * Configuration for strike system
 */
export interface StrikeConfig {
  enabled: boolean;
  strikePeriod: StrikePeriod;
  autoApproveLimit: number; // Max strikes before requiring approval (default: 2)
  blockLimit: number; // Strikes that trigger automatic denial (default: 3)
  resetPeriod: StrikePeriod; // When strikes reset
  requireManagerApproval: boolean;
  escalateToOwner: boolean; // Send urgent alerts to owner
  escalateThreshold: number; // Strike count that triggers owner alert
  expirationMinutes: number; // How long manager has to review (default: 30)
}

/**
 * Default strike configuration
 */
export const DEFAULT_STRIKE_CONFIG: StrikeConfig = {
  enabled: true,
  strikePeriod: 'monthly',
  autoApproveLimit: 2, // First 2 discounts/month auto-approve
  blockLimit: 3, // 3rd+ discount requires approval
  resetPeriod: 'monthly',
  requireManagerApproval: true,
  escalateToOwner: true,
  escalateThreshold: 3,
  expirationMinutes: 30,
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get date range for a strike period
 */
export const getStrikePeriodRange = (period: StrikePeriod): { start: Date; end: Date } => {
  const now = new Date();
  const end = now;
  let start: Date;

  switch (period) {
    case 'weekly':
      start = new Date(now);
      start.setDate(start.getDate() - 7);
      break;
    case 'monthly':
      start = new Date(now);
      start.setMonth(start.getMonth() - 1);
      break;
    case 'quarterly':
      start = new Date(now);
      start.setMonth(start.getMonth() - 3);
      break;
    case 'yearly':
      start = new Date(now);
      start.setFullYear(start.getFullYear() - 1);
      break;
    default:
      start = new Date(now);
      start.setMonth(start.getMonth() - 1);
  }

  return { start, end };
};

/**
 * Convert Firestore Timestamp to Date
 */
const timestampToDate = (timestamp: Date | Timestamp): Date => {
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return timestamp.toDate();
};

/**
 * Generate unique ID for strike
 */
const generateStrikeId = (barberId: string): string => {
  return `strike_${barberId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Generate unique ID for approval request
 */
const generateApprovalRequestId = (barberId: string): string => {
  return `approval_${barberId}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// ============================================================================
// MAIN SERVICE CLASS
// ============================================================================

class DiscountApprovalService {
  private static instance: DiscountApprovalService;

  private constructor() {}

  static getInstance(): DiscountApprovalService {
    if (!DiscountApprovalService.instance) {
      DiscountApprovalService.instance = new DiscountApprovalService();
    }
    return DiscountApprovalService.instance;
  }

  // ==========================================================================
  // STRIKE TRACKING
  // ==========================================================================

  /**
   * Get strike count for a barber within the configured period
   */
  async getStrikeCount(
    barberId: string,
    period: StrikePeriod = 'monthly'
  ): Promise<number> {
    try {
      const { start, end } = getStrikePeriodRange(period);
      const strikesRef = collection(db, 'barberStrikes');
      const q = query(
        strikesRef,
        where('barberId', '==', barberId),
        where('issuedAt', '>=', Timestamp.fromDate(start)),
        where('issuedAt', '<=', Timestamp.fromDate(end))
      );

      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error('Error getting strike count:', error);
      return 0;
    }
  }

  /**
   * Get detailed strike summary for a barber across all periods
   */
  async getStrikeSummary(barberId: string, barberName: string): Promise<StrikeCount> {
    try {
      const [weekly, monthly, quarterly, yearly] = await Promise.all([
        this.getStrikeCount(barberId, 'weekly'),
        this.getStrikeCount(barberId, 'monthly'),
        this.getStrikeCount(barberId, 'quarterly'),
        this.getStrikeCount(barberId, 'yearly'),
      ]);

      // Get last strike date
      const strikesRef = collection(db, 'barberStrikes');
      const q = query(
        strikesRef,
        where('barberId', '==', barberId),
        orderBy('issuedAt', 'desc'),
        limit(1)
      );
      const snapshot = await getDocs(q);
      let lastStrikeDate: Date | null = null;

      if (!snapshot.empty) {
        const lastStrike = snapshot.docs[0].data() as BarberStrike;
        lastStrikeDate = timestampToDate(lastStrike.issuedAt);
      }

      // Calculate next reset date (1 month from last strike)
      let nextResetDate: Date | null = null;
      if (lastStrikeDate) {
        nextResetDate = new Date(lastStrikeDate);
        nextResetDate.setMonth(nextResetDate.getMonth() + 1);
      }

      return {
        barberId,
        barberName,
        weekly,
        monthly,
        quarterly,
        yearly,
        total: yearly, // Total in system
        lastStrikeDate,
        nextResetDate,
      };
    } catch (error) {
      console.error('Error getting strike summary:', error);
      return {
        barberId,
        barberName,
        weekly: 0,
        monthly: 0,
        quarterly: 0,
        yearly: 0,
        total: 0,
        lastStrikeDate: null,
        nextResetDate: null,
      };
    }
  }

  /**
   * Issue a strike to a barber
   */
  async issueStrike(
    barberId: string,
    barberName: string,
    discountRequestId: string,
    appointmentId: string,
    clientId: string,
    fraudAnalysis: FraudAnalysisResult,
    discountRequest: DiscountRequest,
    issuedBy: 'system' | 'manager' | 'owner' = 'system',
    notes?: string
  ): Promise<BarberStrike> {
    try {
      const strikeId = generateStrikeId(barberId);
      const strike: BarberStrike = {
        id: strikeId,
        barberId,
        barberName,
        discountRequestId,
        appointmentId,
        clientId,
        riskScore: fraudAnalysis.riskScore,
        severity: fraudAnalysis.severity,
        fraudFlags: fraudAnalysis.fraudFlags,
        strikeReason: fraudAnalysis.reasoning.join('; '),
        originalPrice: discountRequest.originalPrice,
        discountedPrice: discountRequest.discountedPrice,
        discountAmount: discountRequest.discountAmount,
        discountPercent: discountRequest.discountPercent,
        paymentMethod: discountRequest.paymentMethod,
        issuedAt: Timestamp.now(),
        issuedBy,
        notes,
      };

      const strikeRef = doc(db, 'barberStrikes', strikeId);
      await setDoc(strikeRef, strike);

      console.log(`Strike issued to barber ${barberName} (ID: ${barberId})`);
      return strike;
    } catch (error) {
      console.error('Error issuing strike:', error);
      throw error;
    }
  }

  /**
   * Get all strikes for a barber (with optional limit)
   */
  async getBarberStrikes(
    barberId: string,
    limitCount?: number
  ): Promise<BarberStrike[]> {
    try {
      const strikesRef = collection(db, 'barberStrikes');
      let q = query(
        strikesRef,
        where('barberId', '==', barberId),
        orderBy('issuedAt', 'desc')
      );

      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => {
        const data = doc.data() as BarberStrike;
        return {
          ...data,
          issuedAt: timestampToDate(data.issuedAt),
          appealedAt: data.appealedAt ? timestampToDate(data.appealedAt) : undefined,
        };
      });
    } catch (error) {
      console.error('Error getting barber strikes:', error);
      return [];
    }
  }

  // ==========================================================================
  // APPROVAL WORKFLOW
  // ==========================================================================

  /**
   * Determine if discount requires approval based on strike count and risk
   */
  async requiresApproval(
    barberId: string,
    fraudAnalysis: FraudAnalysisResult,
    config: StrikeConfig = DEFAULT_STRIKE_CONFIG
  ): Promise<{
    requiresApproval: boolean;
    reason: string;
    approvalLevel: 'none' | 'manager' | 'owner';
  }> {
    if (!config.enabled) {
      return {
        requiresApproval: false,
        reason: 'Strike system disabled',
        approvalLevel: 'none',
      };
    }

    // Get current strike count
    const strikeCount = await this.getStrikeCount(barberId, config.strikePeriod);

    // Critical severity always requires owner approval
    if (fraudAnalysis.severity === 'critical') {
      return {
        requiresApproval: true,
        reason: `Critical fraud risk detected (score: ${fraudAnalysis.riskScore})`,
        approvalLevel: 'owner',
      };
    }

    // High severity requires manager approval
    if (fraudAnalysis.severity === 'high') {
      return {
        requiresApproval: true,
        reason: `High fraud risk detected (score: ${fraudAnalysis.riskScore})`,
        approvalLevel: 'manager',
      };
    }

    // Check strike count against limits
    if (strikeCount >= config.blockLimit) {
      return {
        requiresApproval: true,
        reason: `Barber has ${strikeCount} strikes (limit: ${config.blockLimit})`,
        approvalLevel: config.escalateToOwner ? 'owner' : 'manager',
      };
    }

    if (strikeCount >= config.autoApproveLimit) {
      return {
        requiresApproval: true,
        reason: `Barber has ${strikeCount} strikes (auto-approve limit: ${config.autoApproveLimit})`,
        approvalLevel: 'manager',
      };
    }

    // Auto-approve
    return {
      requiresApproval: false,
      reason: `Strike count (${strikeCount}) within auto-approve limit (${config.autoApproveLimit})`,
      approvalLevel: 'none',
    };
  }

  /**
   * Create a discount approval request for manager review
   */
  async createApprovalRequest(
    barberId: string,
    barberName: string,
    discountRequest: DiscountRequest,
    fraudAnalysis: FraudAnalysisResult,
    assignedManagerId?: string,
    assignedManagerName?: string,
    config: StrikeConfig = DEFAULT_STRIKE_CONFIG
  ): Promise<DiscountApprovalRequest> {
    try {
      const requestId = generateApprovalRequestId(barberId);
      const strikeCount = await this.getStrikeCount(barberId, config.strikePeriod);
      const approvalCheck = await this.requiresApproval(barberId, fraudAnalysis, config);

      const expiresAt = new Date();
      expiresAt.setMinutes(expiresAt.getMinutes() + config.expirationMinutes);

      const request: DiscountApprovalRequest = {
        id: requestId,
        barberId,
        barberName,
        discountRequest,
        fraudAnalysis,
        status: 'pending',
        strikeCountAtRequest: strikeCount,
        requiredApprovalLevel: approvalCheck.approvalLevel,
        requestedAt: Timestamp.now(),
        assignedManagerId,
        assignedManagerName,
        notificationsSent: [],
        expiresAt: Timestamp.fromDate(expiresAt),
        isExpired: false,
      };

      const requestRef = doc(db, 'discountRequests', requestId);
      await setDoc(requestRef, request);

      console.log(`Approval request created: ${requestId} for barber ${barberName}`);
      return request;
    } catch (error) {
      console.error('Error creating approval request:', error);
      throw error;
    }
  }

  /**
   * Get pending approval requests (for manager dashboard)
   */
  async getPendingApprovals(managerId?: string): Promise<DiscountApprovalRequest[]> {
    try {
      const requestsRef = collection(db, 'discountRequests');
      let q = query(
        requestsRef,
        where('status', '==', 'pending'),
        orderBy('requestedAt', 'desc')
      );

      if (managerId) {
        q = query(q, where('assignedManagerId', '==', managerId));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => {
        const data = doc.data() as DiscountApprovalRequest;
        return {
          ...data,
          requestedAt: timestampToDate(data.requestedAt),
          reviewedAt: data.reviewedAt ? timestampToDate(data.reviewedAt) : undefined,
          expiresAt: data.expiresAt ? timestampToDate(data.expiresAt) : undefined,
        };
      });
    } catch (error) {
      console.error('Error getting pending approvals:', error);
      return [];
    }
  }

  /**
   * Approve or deny a discount request
   */
  async reviewApprovalRequest(
    requestId: string,
    decision: ApprovalDecision
  ): Promise<DiscountApprovalRequest> {
    try {
      const requestRef = doc(db, 'discountRequests', requestId);
      const requestDoc = await getDoc(requestRef);

      if (!requestDoc.exists()) {
        throw new Error(`Approval request ${requestId} not found`);
      }

      const request = requestDoc.data() as DiscountApprovalRequest;

      // Check if already reviewed
      if (request.status !== 'pending') {
        throw new Error(`Request ${requestId} already ${request.status}`);
      }

      // Check if expired
      if (request.expiresAt) {
        const expiryDate = timestampToDate(request.expiresAt);
        if (new Date() > expiryDate) {
          await updateDoc(requestRef, {
            status: 'expired',
            isExpired: true,
          });
          throw new Error(`Request ${requestId} has expired`);
        }
      }

      // Update request with decision
      const updates: Partial<DiscountApprovalRequest> = {
        status: decision.approved ? 'manager_approved' : 'denied',
        reviewedAt: Timestamp.now(),
        reviewedBy: decision.reviewedBy,
        reviewerRole: decision.reviewerRole,
        approvalNotes: decision.approved ? decision.notes : undefined,
        denialReason: decision.approved ? undefined : decision.notes,
      };

      await updateDoc(requestRef, updates);

      // Issue strike if denied or flagged
      if (decision.strikeIssued && decision.strikeReason) {
        await this.issueStrike(
          request.barberId,
          request.barberName,
          requestId,
          request.discountRequest.appointmentId,
          request.discountRequest.clientId,
          request.fraudAnalysis,
          request.discountRequest,
          decision.reviewerRole === 'owner' ? 'owner' : 'manager',
          decision.strikeReason
        );
      }

      console.log(
        `Request ${requestId} ${decision.approved ? 'approved' : 'denied'} by ${decision.reviewedBy}`
      );

      return {
        ...request,
        ...updates,
        requestedAt: timestampToDate(request.requestedAt),
        reviewedAt: new Date(),
        expiresAt: request.expiresAt ? timestampToDate(request.expiresAt) : undefined,
      };
    } catch (error) {
      console.error('Error reviewing approval request:', error);
      throw error;
    }
  }

  /**
   * Auto-approve a discount (under strike limit)
   */
  async autoApproveDiscount(
    barberId: string,
    barberName: string,
    discountRequest: DiscountRequest,
    fraudAnalysis: FraudAnalysisResult
  ): Promise<DiscountApprovalRequest> {
    try {
      const requestId = generateApprovalRequestId(barberId);
      const strikeCount = await this.getStrikeCount(barberId, DEFAULT_STRIKE_CONFIG.strikePeriod);

      const request: DiscountApprovalRequest = {
        id: requestId,
        barberId,
        barberName,
        discountRequest,
        fraudAnalysis,
        status: 'auto_approved',
        strikeCountAtRequest: strikeCount,
        requiredApprovalLevel: 'none',
        requestedAt: Timestamp.now(),
        reviewedAt: Timestamp.now(),
        reviewedBy: 'system',
        reviewerRole: 'manager',
        approvalNotes: `Auto-approved: ${strikeCount} strikes within limit`,
        notificationsSent: [],
        isExpired: false,
      };

      const requestRef = doc(db, 'discountRequests', requestId);
      await setDoc(requestRef, request);

      console.log(`Discount auto-approved for barber ${barberName} (${strikeCount} strikes)`);
      return request;
    } catch (error) {
      console.error('Error auto-approving discount:', error);
      throw error;
    }
  }

  // ==========================================================================
  // MANAGER DASHBOARD UTILITIES
  // ==========================================================================

  /**
   * Get approval request by ID
   */
  async getApprovalRequest(requestId: string): Promise<DiscountApprovalRequest | null> {
    try {
      const requestRef = doc(db, 'discountRequests', requestId);
      const requestDoc = await getDoc(requestRef);

      if (!requestDoc.exists()) {
        return null;
      }

      const data = requestDoc.data() as DiscountApprovalRequest;
      return {
        ...data,
        requestedAt: timestampToDate(data.requestedAt),
        reviewedAt: data.reviewedAt ? timestampToDate(data.reviewedAt) : undefined,
        expiresAt: data.expiresAt ? timestampToDate(data.expiresAt) : undefined,
      };
    } catch (error) {
      console.error('Error getting approval request:', error);
      return null;
    }
  }

  /**
   * Get all approval requests for a barber (audit trail)
   */
  async getBarberApprovalHistory(
    barberId: string,
    limitCount?: number
  ): Promise<DiscountApprovalRequest[]> {
    try {
      const requestsRef = collection(db, 'discountRequests');
      let q = query(
        requestsRef,
        where('barberId', '==', barberId),
        orderBy('requestedAt', 'desc')
      );

      if (limitCount) {
        q = query(q, limit(limitCount));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => {
        const data = doc.data() as DiscountApprovalRequest;
        return {
          ...data,
          requestedAt: timestampToDate(data.requestedAt),
          reviewedAt: data.reviewedAt ? timestampToDate(data.reviewedAt) : undefined,
          expiresAt: data.expiresAt ? timestampToDate(data.expiresAt) : undefined,
        };
      });
    } catch (error) {
      console.error('Error getting barber approval history:', error);
      return [];
    }
  }

  /**
   * Mark notification as sent for approval request
   */
  async markNotificationSent(
    requestId: string,
    channel: NotificationChannel
  ): Promise<void> {
    try {
      const requestRef = doc(db, 'discountRequests', requestId);
      const requestDoc = await getDoc(requestRef);

      if (requestDoc.exists()) {
        const request = requestDoc.data() as DiscountApprovalRequest;
        const notificationsSent = [...request.notificationsSent, channel];
        await updateDoc(requestRef, { notificationsSent });
      }
    } catch (error) {
      console.error('Error marking notification sent:', error);
    }
  }
}

// ============================================================================
// EXPORT SINGLETON
// ============================================================================

export const discountApprovalService = DiscountApprovalService.getInstance();
export default discountApprovalService;
